/**
 * An immutable 2D point. Small on purpose: the game needs eight operations and
 * every one of them returns a new vector, so a position can be handed around
 * without anybody being able to move it.
 */
export class Vector {
  static of(x: number, y: number) {
    return new Vector(x, y);
  }

  static fromRadians(radians: number, magnitude: number) {
    return new Vector(
      Math.cos(radians) * magnitude,
      Math.sin(radians) * magnitude,
    );
  }

  readonly x: number;
  readonly y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  get isZero() {
    return this.x === 0 && this.y === 0;
  }

  get magnitude() {
    return Math.hypot(this.x, this.y);
  }

  /** Clockwise from the positive X axis, because Y grows downwards on canvas. */
  get radians() {
    return Math.atan2(this.y, this.x);
  }

  add({ x, y }: Vector) {
    return new Vector(this.x + x, this.y + y);
  }

  sub({ x, y }: Vector) {
    return new Vector(this.x - x, this.y - y);
  }

  scale(value: number) {
    return new Vector(this.x * value, this.y * value);
  }

  setMagnitude(value: number) {
    const { magnitude } = this;
    return magnitude === 0
      ? new Vector(value, 0)
      : this.scale(value / magnitude);
  }

  distance(target: Vector) {
    return this.sub(target).magnitude;
  }
}
